import type { Swatches } from '@dealerportal/common';
import { ParsedCloudinaryURL } from '@dealerportal/utilities';
import classNames from 'classnames';
import { useCallback } from 'react';
import { MissingImage } from './MissingImage';
import { ProductLink } from './ProductLink';

type Props = {
  selectedSwatch: Swatches;
  swatches: Swatches[];
  onMouseEnter: (swatch: Swatches) => void;
  onSelect: (swatch: Swatches) => void;
  itemClassName?: string;
  listClassName?: string;
};
export const ProductCardSwatches = ({
  swatches,
  selectedSwatch,
  onSelect,
  onMouseEnter,
  itemClassName,
  listClassName,
}: Props) => {
  const transformImage = useCallback((url: string) => {
    if (url?.includes('res.cloudinary.com')) {
      const parsed = new ParsedCloudinaryURL(url, true);
      parsed.RemoveTransformation('w');
      parsed.RemoveTransformation('h');
      parsed.AddTransformation('w_40');
      parsed.AddTransformation('h_40');
      return parsed.GetURL();
    }
    return url;
  }, []);

  const renderImage = useCallback(
    (swatch: Swatches) => {
      if (!swatch?.imageURL) {
        return (
          <MissingImage
            className="!ui-relative ui-fill-gray-400/50"
            width={40}
            height={40}
          />
        );
      }
      return (
        <img
          className="!ui-relative ui-object-center"
          onClick={(e) => {
            onSelect(swatch);
            e.stopPropagation();
            e.preventDefault();
          }}
          src={transformImage(swatch?.imageURL)}
          alt={swatch?.colorName}
          height="40"
          width="40"
        />
      );
    },
    [transformImage, onSelect]
  );
  return (
    <ul
      data-cy="swatch-list"
      className={
        listClassName ??
        'ui-grid ui-grid-cols-7 ui-gap-2 ui-list-none ui-p-0 ui-m-0'
      }
    >
      {swatches.map((swatch) => (
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events
        <li
          onMouseEnter={() => onMouseEnter(swatch)}
          key={swatch.UPID}
          title={swatch.colorName}
          className={classNames(
            'ui-shrink-0 ui-outline ui-outline-2 ui-outline-transparent ui-overflow-hidden',
            itemClassName ?? 'ui-h-6 ui-w-6 ui-rounded-full',
            {
              '!ui-outline-[var(--cta-color,#030712)]':
                swatch?.UPID === selectedSwatch?.UPID,
            }
          )}
        >
          <ProductLink
            product={{ slug: swatch.slug, sku: swatch.sku }}
            className="ui-h-6 ui-w-6 ui-flex ui-items-center ui-justify-center ui-leading-0"
          >
            {renderImage(swatch)}
          </ProductLink>
        </li>
      ))}
    </ul>
  );
};
